/**
 * PLT-7846 — the "Ask Upright to review this" card, drawn by the chat-only `request_upright_review`
 * tool with its fields filled in from the conversation. The user edits them and sends the request to
 * the Upright team, or cancels. The card promises a look, not a reply (PLT-7888).
 *
 * The tool sends nothing: the card POSTs the answer itself (`use-review-request.ts`), and reads it
 * back on mount, so a reopened conversation shows a sent or cancelled card as one closed line
 * instead of the form. A card left unanswered comes back sendable.
 *
 * Spacing is the standard widget row (`xl` inset, `xl` between header, body and footer) with the
 * `lg` title pairing; the `/internal/proto/chat-flag-to-upright` prototype holds every state still.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ReviewRequestFields } from '@upright/common/chat/review-request-types';
import {
  REVIEW_REQUEST_FIELD_MAX_LENGTH,
  REVIEW_REQUEST_SOURCE_MAX_LENGTH,
} from '@upright/common/chat/review-request-types';
import { Button } from '@upright/ui/common/generic/components/button';
import { Input, TextArea } from '@upright/ui/common/generic/components/input';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Heading, Paragraph, Text } from '@upright/ui/common/generic/components/typography';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { getReviewRequestStateQuery, useAnswerReviewRequestMutation } from './use-review-request';
import styles from './review-request-card.module.css';

interface Props {
  conversationId: string;
  /** The assistant message the card is in: the card's identity, live and reopened alike. */
  messageIndex: number;
  /** What the tool filled in. The card's starting values; the user's edits are its own. */
  draft: ReviewRequestFields;
}

export function ReviewRequestCard({ conversationId, messageIndex, draft }: Props) {
  const titleId = useId();
  const pushGtmEvent = usePushGtmEvent();
  const stateQuery = useQuery(getReviewRequestStateQuery(conversationId, messageIndex));
  const answer = useAnswerReviewRequestMutation(conversationId, messageIndex);

  const [question, setQuestion] = useState(draft.question);
  const [problem, setProblem] = useState(draft.problem);
  const [expected, setExpected] = useState(draft.expected);
  const [source, setSource] = useState(draft.source);

  const status = stateQuery.data?.status ?? null;

  // Counted once per drawn form, against the sent and cancelled events below.
  const shownLogged = useRef(false);
  useEffect(() => {
    if (shownLogged.current || !stateQuery.isSuccess || status !== null) return;
    shownLogged.current = true;
    pushGtmEvent({ event: CustomGtmEvent.CHAT_REVIEW_REQUEST_SHOWN });
  }, [stateQuery.isSuccess, status, pushGtmEvent]);

  if (stateQuery.isPending) {
    return null;
  }

  if (status !== null) {
    return (
      <div className={styles.outcome}>
        <Paragraph size="md" height="sm" color="gray-800">
          {status === 'sent'
            ? 'Sent to the Upright team for review.'
            : 'Not sent. You can ask Upright to review this at any time.'}
        </Paragraph>
      </div>
    );
  }

  const fields = { question: question.trim(), problem: problem.trim(), expected: expected.trim() };
  const isComplete = fields.question !== '' && fields.problem !== '' && fields.expected !== '';

  const send = (event: FormEvent) => {
    event.preventDefault();
    if (!isComplete) return;
    answer.mutate(
      { status: 'sent', fields: { ...fields, source: source.trim() } },
      { onSuccess: () => pushGtmEvent({ event: CustomGtmEvent.CHAT_REVIEW_REQUEST_SENT }) },
    );
  };

  const cancel = () => {
    answer.mutate(
      { status: 'cancelled' },
      { onSuccess: () => pushGtmEvent({ event: CustomGtmEvent.CHAT_REVIEW_REQUEST_CANCELLED }) },
    );
  };

  return (
    <form className={styles.card} aria-labelledby={titleId} onSubmit={send}>
      <Stack gap="md">
        <Heading id={titleId} level={3} size="lg" height="lg" weight="semibold" color="gray-900">
          Ask Upright to review this
        </Heading>
        <Paragraph size="md" height="sm" color="gray-600">
          The Upright team will look into it.
        </Paragraph>
      </Stack>
      <Stack gap="lg">
        <Paragraph size="md" height="sm" color="gray-800">
          We’ve filled this in from your conversation. Edit anything that’s off.
        </Paragraph>
        <TextArea
          label="Your question"
          rows={2}
          value={question}
          onChange={setQuestion}
          maxLength={REVIEW_REQUEST_FIELD_MAX_LENGTH}
        />
        <TextArea
          label="What looks wrong"
          rows={2}
          value={problem}
          onChange={setProblem}
          maxLength={REVIEW_REQUEST_FIELD_MAX_LENGTH}
        />
        <TextArea
          label="What you’d expect instead"
          rows={2}
          value={expected}
          onChange={setExpected}
          maxLength={REVIEW_REQUEST_FIELD_MAX_LENGTH}
        />
        <Input
          label="Source (optional)"
          description="A link that backs this up, e.g. a website"
          value={source}
          onChange={setSource}
          maxLength={REVIEW_REQUEST_SOURCE_MAX_LENGTH}
        />
      </Stack>
      <Stack gap="lg">
        <Paragraph size="sm" height="xs" color="gray-600">
          The Upright team will see this summary, the full conversation, and your name and email.
        </Paragraph>
        {answer.isError && (
          <div role="alert">
            <Text size="sm" height="xs" color="red-500">
              {answer.variables.status === 'sent'
                ? 'The request could not be sent. Try again.'
                : 'Something went wrong. Try again.'}
            </Text>
          </div>
        )}
        <div className={styles.actions}>
          <Button variant="outlined" onPress={cancel} isDisabled={answer.isPending}>
            Cancel
          </Button>
          <Button type="submit" isDisabled={!isComplete} isPending={answer.isPending}>
            Send for review
          </Button>
        </div>
      </Stack>
    </form>
  );
}
